'use client';

import { useMemo, useState } from 'react';

import styles from './shareLinks.module.scss';

import { ShareTriggerIcon } from './icons';
import {
  getPlatforms,
  type SharePlatform,
  type SharePlatformId
} from './platforms';

export type ShareLinksProps = {
  className?: string;
  disabled?: boolean;
  isSocialMediaShareable?: boolean;
  onlyCopy?: boolean;
  tooltipLabel?: string;
  link?: string;
  onCopy?: () => void | Promise<void>;
  onEmail?: () => void | Promise<void>;
  onSms?: () => void | Promise<void>;
  onSocialShare?: (
    platform: Exclude<SharePlatformId, 'copy' | 'email' | 'sms'>
  ) => void | Promise<void>;
};

const cx = (...parts: Array<string | false | null | undefined>) =>
  parts.filter(Boolean).join(' ');

export function ShareLinks({
  className,
  disabled = false,
  isSocialMediaShareable = false,
  onlyCopy = false,
  tooltipLabel = 'Share',
  link,
  onCopy,
  onEmail,
  onSms,
  onSocialShare
}: ShareLinksProps) {
  const platforms = useMemo(
    () => getPlatforms(isSocialMediaShareable, onlyCopy),
    [isSocialMediaShareable, onlyCopy]
  );

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const isSinglePlatform = platforms.length === 1;

  const toggleMenu = (event?: React.SyntheticEvent) => {
    event?.preventDefault();
    event?.stopPropagation();
    if (disabled) return;
    setIsMenuOpen((prev) => !prev);
  };

  const closeMenu = () => setIsMenuOpen(false);

  const copyLink = async () => {
    if (onCopy) {
      await onCopy();
      return;
    }

    if (!link) return;

    try {
      await navigator.clipboard.writeText(link);
    } catch {
      // no-op
    }
  };

  const selectPlatform = async (
    event: React.SyntheticEvent,
    platform: SharePlatform
  ) => {
    event.preventDefault();
    event.stopPropagation();

    if (disabled) return;

    switch (platform.method) {
      case 'share':
        await onSocialShare?.(
          platform.id as 'twitter' | 'facebook' | 'instagram'
        );
        break;
      case 'sms':
        await onSms?.();
        break;
      case 'email':
        await onEmail?.();
        break;
      case 'copy':
        await copyLink();
        break;
      default:
        break;
    }

    closeMenu();
  };

  return (
    <div
      tabIndex={0}
      className={cx(styles.root, className)}
      title={!isMenuOpen ? tooltipLabel : undefined}
      onClick={(event) => toggleMenu(event)}
      onBlur={(event) => {
        const nextFocus = event.relatedTarget as Node | null;
        if (nextFocus && event.currentTarget.contains(nextFocus)) return;
        closeMenu();
      }}
    >
      <div
        className={cx(
          styles.menu,
          isMenuOpen && styles.menuOpen,
          isMenuOpen && isSinglePlatform && styles.menuOpenOne,
          disabled && styles.menuDisabled
        )}
      >
        <div className={styles.link}>
          <ShareTriggerIcon className={styles.shareIcon} />
        </div>

        <div
          className={cx(
            styles.platforms,
            !isMenuOpen && styles.platformsClosed
          )}
        >
          {platforms.map((platform) => (
            <button
              key={platform.id}
              type="button"
              className={styles.platformButton}
              aria-label={platform.label}
              onClick={(event) => {
                void selectPlatform(event, platform);
              }}
            >
              <span className={styles.platformIcon}>{platform.icon}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
